import { Banner, Button, FlexBox, Text } from '@dixa/lib-shared-habla';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArticleDataContext } from 'articleEditor/contexts/articleDataProvider';
import { ArticleSavingContext } from 'articleEditor/contexts/savingContext';
import { TranslationDataContext } from 'articleEditor/contexts/translationProvider';
import { useIsTranslating } from 'articleEditor/hooks/translations/useIsTranslating';
import { FieldInputText, FieldInputTextArea } from 'lib-knowledge-form';
import {
  useArticleTranslationHelpCentersQuery,
  useArticleTranslationSeoUpdateMutation,
} from 'lib-knowledge-management-common/graphql';
import { useNotification } from 'lib-knowledge-utils/hooks/useNotification';
import { useContext } from 'react';
import { useForm } from 'react-hook-form';
import { useSnapshot } from 'valtio';
import { z } from 'zod';

import { useNavigatePrompt } from 'hooks/useNavigatePrompt';

import s from './seoSidebar.module.scss';

const seoFormSchema = z.object({
  seoTitle: z.string().max(300, 'Use at most 300 characters'),
  metaDescription: z.string().max(1000, 'Use at most 1000 characters'),
  slug: z
    .string()
    .refine(
      (slug) => slug === '' || /[\p{L}\p{M}\p{N}]/u.test(slug),
      'Use at least one letter or digit',
    ),
});

type SeoForm = z.infer<typeof seoFormSchema>;

type SeoSidebarProps = {
  hidden: boolean;
};

export function SeoSidebar({ hidden }: SeoSidebarProps): JSX.Element {
  const article = useContext(ArticleDataContext);
  const { draft } = useContext(TranslationDataContext);
  const { savingStore } = useContext(ArticleSavingContext);
  const {
    currentArticleDetails: { title },
  } = useSnapshot(savingStore);
  const isTranslating = useIsTranslating();
  const { errorNotify } = useNotification();
  const [updateSeo] = useArticleTranslationSeoUpdateMutation();
  const { data: helpCenters } = useArticleTranslationHelpCentersQuery({
    variables: { id: article.id, languageId: draft.languageId },
    skip: hidden,
  });

  const {
    control,
    reset,
    handleSubmit,
    formState: { isDirty, isValid, isSubmitting, dirtyFields },
  } = useForm<SeoForm>({
    resolver: zodResolver(seoFormSchema),
    defaultValues: {
      seoTitle: draft.seoTitle ?? '',
      metaDescription: draft.metaDescription ?? '',
      slug: draft.slug ?? '',
    },
    mode: 'all',
  });

  useNavigatePrompt(isDirty);

  const onSubmit = async (values: SeoForm) => {
    try {
      const { data } = await updateSeo({
        variables: {
          articleId: article.id,
          languageId: draft.languageId,
          ...Object.fromEntries(
            Object.keys(dirtyFields).map((field) => [
              field,
              values[field as keyof SeoForm],
            ]),
          ),
        },
        refetchQueries: ['ArticleTranslation'],
      });
      const stored = data?.articleTranslationUpdate ?? draft;
      reset({
        seoTitle: stored.seoTitle ?? '',
        metaDescription: stored.metaDescription ?? '',
        slug: stored.slug ?? '',
      });
    } catch {
      errorNotify({ text: 'Saving the SEO settings failed' });
    }
  };

  const disabled = !!article.isSyncedWithElevio || isTranslating;

  return (
    <aside className={s.root} hidden={hidden}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FlexBox flexDirection="column" gap="300" padding="300">
          {helpCenters?.article?.translation?.draft.availableAt.helpCenters
            .length === 0 && (
            <Banner
              variant="info"
              icon="info"
              description={
                <Text.Paragraph as="span" variant="3">
                  This article isn&apos;t on a help center, so these settings
                  have no effect yet.
                </Text.Paragraph>
              }
            />
          )}
          <FieldInputText
            control={control}
            name="seoTitle"
            label="SEO title"
            helpIcon={{
              text: 'The title search engines show for this article. When empty, the article title is used.',
            }}
            placeholder={title}
            disabled={disabled}
          />
          <FieldInputTextArea
            control={control}
            name="metaDescription"
            label="Meta description"
            helpIcon={{
              text: 'The summary search engines show under the title. When empty, they write their own from the article.',
            }}
            height={96}
            width="100%"
            disabled={disabled}
          />
          <FieldInputText
            control={control}
            name="slug"
            label="URL slug"
            prefix={`${draft.path.split('-')[0]}-`}
            placeholder={draft.slug ? '' : (draft.effectiveSlug ?? '')}
            description="Built from the title until you change it"
            disabled={disabled}
          />
          <FlexBox justifyContent="end">
            <Button
              type="submit"
              disabled={disabled || !isDirty || !isValid}
              isLoading={isSubmitting}
            >
              Save
            </Button>
          </FlexBox>
        </FlexBox>
      </form>
    </aside>
  );
}
